import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

// ── Time-of-day awareness ─────────────────────────────────────────────────
const getTimeContext = () => {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
};

// Which protocol is contextually recommended right now
const getRecommended = (ctx) => {
  if (ctx === "morning" || ctx === "afternoon") return "neuroplasticity";
  if (ctx === "evening" || ctx === "night")     return "sleep";
  return "neuroplasticity";
};

// ── Protocol definitions ──────────────────────────────────────────────────
const PROTOCOLS = [
  {
    id: "neuroplasticity",
    route: "/protocol/neuroplasticity",
    label: "NEUROPLASTICITY RESET",
    duration: "15 min",
    tagline: "Train your brain's operating state",
    description: "A three-phase sequence that resets your autonomic nervous system, opens a neuroplastic window, and installs a chosen leadership state.",
    phases: ["RESET", "LOCK", "INSTALL"],
    phaseColors: ["#4A90D9", "#C9A84C", "#4CAF7A"],
    accentColor: "#C9A84C",
    borderColor: "rgba(201,168,76,0.25)",
    bgColor: "rgba(201,168,76,0.04)",
    glowColor: "rgba(201,168,76,0.12)",
    bestFor: "Morning · After waking",
    timeCtx: ["morning", "afternoon"],
  },
  {
    id: "highstakes",
    route: "/firewall",
    label: "HIGH STAKES READY",
    duration: "3 min",
    tagline: "Reset before what matters most",
    description: "A voice-guided pranayama sequence designed for a bathroom, stairwell, or any 3 minutes before a high-stakes meeting or decision.",
    phases: ["BREATHE", "CENTRE"],
    phaseColors: ["#D4723A", "#D4723A"],
    accentColor: "#D4723A",
    borderColor: "rgba(212,114,58,0.25)",
    bgColor: "rgba(212,114,58,0.04)",
    glowColor: "rgba(212,114,58,0.12)",
    bestFor: "Before meetings · High pressure moments",
    timeCtx: [], // always available, never auto-recommended
  },
  {
    id: "sleep",
    route: "/sleep",
    label: "SLEEP DESCENT",
    duration: "5 min",
    tagline: "Clear the day and descend into deep sleep",
    description: "Cognitive offload via voice journal, followed by a pranayama sequence that drives parasympathetic dominance and sleep onset.",
    phases: ["OFFLOAD", "DESCEND"],
    phaseColors: ["#5B4FCF", "#9B59B6"],
    accentColor: "#5B4FCF",
    borderColor: "rgba(91,79,207,0.25)",
    bgColor: "rgba(91,79,207,0.04)",
    glowColor: "rgba(91,79,207,0.12)",
    bestFor: "30–60 min before sleep",
    timeCtx: ["evening", "night"],
  },
];

// ── Protocol Card ─────────────────────────────────────────────────────────
const ProtocolCard = ({ protocol, recommended, index, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const isActive = !protocol.comingSoon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: [0.23, 1, 0.32, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => isActive && onClick(protocol.route)}
      className="relative rounded-sm transition-all duration-300"
      style={{
        background: hovered && isActive ? protocol.bgColor : "rgba(28,33,48,0.6)",
        border: `1px solid ${hovered && isActive ? protocol.borderColor : "rgba(255,255,255,0.07)"}`,
        cursor: isActive ? "pointer" : "default",
        boxShadow: hovered && isActive ? `0 0 40px ${protocol.glowColor}` : "none",
      }}
      data-testid={`protocol-card-${protocol.id}`}
    >
      {/* Recommended badge */}
      {recommended && (
        <div
          className="absolute -top-px left-6 px-3 py-1 text-[9px] font-medium uppercase tracking-[0.18em]"
          style={{
            background: protocol.accentColor,
            color: "#0D0F14",
            fontFamily: "DM Mono, monospace",
            borderRadius: "0 0 4px 4px",
          }}
        >
          Recommended now
        </div>
      )}

      {/* Coming soon overlay */}
      {protocol.comingSoon && (
        <div className="absolute inset-0 rounded-sm flex items-center justify-end pr-6"
          style={{ pointerEvents: "none" }}>
          <span
            className="text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm"
            style={{
              fontFamily: "DM Mono, monospace",
              background: "rgba(91,79,207,0.12)",
              color: "rgba(91,79,207,0.7)",
              border: "1px solid rgba(91,79,207,0.2)",
            }}
          >
            Coming soon
          </span>
        </div>
      )}

      <div className="p-6 md:p-8">
        {/* Header row */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.22em] mb-2"
              style={{
                fontFamily: "DM Mono, monospace",
                color: protocol.comingSoon ? "rgba(91,79,207,0.5)" : protocol.accentColor,
              }}
            >
              {protocol.label}
            </p>
            <h2
              className="text-2xl md:text-3xl tracking-tight"
              style={{
                fontFamily: "DM Serif Display, Georgia, serif",
                color: protocol.comingSoon ? "rgba(237,232,223,0.35)" : "#e8e2d6",
                fontWeight: 400,
              }}
            >
              {protocol.tagline}
            </h2>
          </div>

          {/* Duration pill */}
          <div
            className="flex-shrink-0 ml-4 px-3 py-1.5 rounded-sm text-right"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <p
              className="text-[18px] leading-none"
              style={{
                fontFamily: "DM Mono, monospace",
                color: protocol.comingSoon ? "rgba(237,232,223,0.2)" : "#e8e2d6",
              }}
            >
              {protocol.duration}
            </p>
          </div>
        </div>

        {/* Description */}
        <p
          className="text-[13px] leading-relaxed mb-6"
          style={{
            color: protocol.comingSoon ? "rgba(237,232,223,0.2)" : "rgba(237,232,223,0.55)",
            maxWidth: 520,
          }}
        >
          {protocol.description}
        </p>

        {/* Phase pills */}
        <div className="flex items-center gap-2 mb-5">
          {protocol.phases.map((phase, i) => (
            <span key={phase} className="flex items-center gap-2">
              <span
                className="px-2.5 py-1 text-[9px] uppercase tracking-[0.14em] rounded-sm"
                style={{
                  fontFamily: "DM Mono, monospace",
                  background: protocol.comingSoon
                    ? "rgba(255,255,255,0.03)"
                    : `${protocol.phaseColors[i]}15`,
                  color: protocol.comingSoon
                    ? "rgba(237,232,223,0.15)"
                    : protocol.phaseColors[i],
                  border: `1px solid ${protocol.comingSoon ? "rgba(255,255,255,0.05)" : `${protocol.phaseColors[i]}30`}`,
                }}
              >
                {phase}
              </span>
              {i < protocol.phases.length - 1 && (
                <span style={{ color: "rgba(255,255,255,0.12)", fontSize: 10 }}>›</span>
              )}
            </span>
          ))}
        </div>

        {/* Footer row */}
        <div className="flex items-center justify-between">
          <p
            className="text-[11px]"
            style={{
              fontFamily: "DM Mono, monospace",
              color: protocol.comingSoon ? "rgba(237,232,223,0.15)" : "rgba(237,232,223,0.3)",
            }}
          >
            {protocol.bestFor}
          </p>

          {isActive && (
            <motion.div
              animate={{ x: hovered ? 4 : 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-1.5"
              style={{ color: protocol.accentColor }}
            >
              <span className="text-[11px]" style={{ fontFamily: "DM Mono, monospace" }}>
                Begin
              </span>
              <span style={{ fontSize: 14 }}>›</span>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────
export const ProtocolSelect = ({ user }) => {
  const navigate = useNavigate();
  const timeCtx = getTimeContext();
  const recommended = getRecommended(timeCtx);

  const timeLabel = {
    morning:   "Good morning",
    afternoon: "Good afternoon",
    evening:   "Good evening",
    night:     "Good evening",
  }[timeCtx];

  const firstName = user?.name?.split(" ")[0] || "Practitioner";

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--bg, #08090b)" }}
    >
      {/* Back to dashboard */}
      <header className="px-6 md:px-10 pt-6 pb-2 flex items-center">
        <button
          onClick={() => navigate("/progress")}
          className="flex items-center gap-2 transition-opacity hover:opacity-70"
          style={{ color: "rgba(237,232,223,0.3)", fontFamily: "DM Mono, monospace", fontSize: 11 }}
        >
          ‹ Dashboard
        </button>
      </header>

      <main className="flex-1 px-6 md:px-10 py-6 max-w-3xl w-full mx-auto">

        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
          className="mb-10"
        >
          <p
            className="text-[11px] uppercase tracking-[0.22em] mb-2"
            style={{ fontFamily: "DM Mono, monospace", color: "rgba(237,232,223,0.35)" }}
          >
            {timeLabel}, {firstName}
          </p>
          <h1
            className="text-3xl md:text-4xl tracking-tight"
            style={{
              fontFamily: "DM Serif Display, Georgia, serif",
              color: "#e8e2d6",
              fontWeight: 400,
            }}
          >
            Choose your protocol
          </h1>
          <p
            className="text-[13px] mt-3"
            style={{ color: "rgba(237,232,223,0.4)", maxWidth: 420 }}
          >
            Each protocol is designed for a specific moment in your day and a specific neurological outcome.
          </p>
        </motion.div>

        {/* Protocol cards */}
        <div className="space-y-4">
          {PROTOCOLS.map((protocol, i) => (
            <ProtocolCard
              key={protocol.id}
              protocol={protocol}
              recommended={recommended === protocol.id}
              index={i}
              onClick={(route) => navigate(route)}
            />
          ))}
        </div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-center text-[11px] mt-10"
          style={{ color: "rgba(237,232,223,0.2)", fontFamily: "DM Mono, monospace" }}
        >
          All protocols are grounded in Vedic neuroscience and validated through EEG measurement.
        </motion.p>

      </main>
    </div>
  );
};
